import type { TTimelineItem } from "../types";

export interface AnnotationFocus {
  /** Canonical node IDs, including descendants of a focused collection. */
  ids: ReadonlySet<string>;
  showLabel: boolean;
  /** Whether this focus includes box targets, even when none are visible on the current frame. */
  hasBoxTargets: boolean;
}

export type AnnotationFocusIndex = ReadonlyMap<string, AnnotationFocus>;

export interface ResolvedAnnotationFocus extends AnnotationFocus {
  mode: "hover" | "pinned";
}

/** Waypoint, decoded-frame and cluster IDs all address the same track. */
export function focusTargetId(id: string): string {
  return id.replace(/::(?:c|wp|frame)::.*$/, "");
}

/** Only tracks and collections containing tracks participate in video focus. */
export function isTimelineFocusTarget(item: TTimelineItem | null | undefined): boolean {
  return !!item && (item.type === "track" || (item.type === "collection" && item.children.some(isTimelineFocusTarget)));
}

/** Build once per annotation revision; playback only checks visible boxes against these IDs. */
export function createFocusIndex(items: readonly TTimelineItem[]): AnnotationFocusIndex {
  const index = new Map<string, AnnotationFocus>();
  const visit = (item: TTimelineItem): AnnotationFocus | null => {
    if (item.type !== "track" && item.type !== "collection") return null;
    const ids = new Set([item.id]);
    let hasBoxTargets =
      item.type === "track" && (item.waypointKind ?? item.waypoints[0]?.kind ?? "point_box") === "point_box";
    if (item.type === "collection") {
      for (const child of item.children) {
        const childFocus = visit(child);
        if (!childFocus) continue;
        for (const id of childFocus.ids) ids.add(id);
        hasBoxTargets ||= childFocus.hasBoxTargets;
      }
      if (ids.size === 1) return null;
    }
    const focus = { ids, showLabel: item.type === "track", hasBoxTargets };
    index.set(item.id, focus);
    return focus;
  };
  items.forEach(visit);
  return index;
}

/** Collection wrappers do not change which leaf annotations a scope addresses. */
function sameMemberScope(index: AnnotationFocusIndex, first: AnnotationFocus, second: AnnotationFocus): boolean {
  if (first === second) return true;
  // The index gives default labels to leaves, never collection/video wrappers.
  const isMember = (id: string) => index.get(id)?.showLabel === true;
  const members = [...first.ids].filter(isMember);
  return (
    members.length > 0 &&
    members.length === [...second.ids].filter(isMember).length &&
    members.every((id) => second.ids.has(id))
  );
}

/** Hover previews temporarily replace the pin; leaving the preview restores it. */
export function resolveAnnotationFocus(
  index: AnnotationFocusIndex,
  selectedId: string | null,
  highlightedIds: readonly string[],
): ResolvedAnnotationFocus | null {
  const lookup = (id: string) => index.get(id) ?? index.get(focusTargetId(id));
  const selected = selectedId ? lookup(selectedId) : undefined;
  const highlighted = [...new Set(highlightedIds.map(lookup).filter((focus) => focus !== undefined))];
  if (highlighted.length === 0) {
    return selected ? { ...selected, showLabel: true, mode: "pinned" } : null;
  }
  const hovered =
    highlighted.length === 1
      ? highlighted[0]!
      : {
          ids: new Set(highlighted.flatMap((focus) => [...focus.ids])),
          showLabel: false,
          hasBoxTargets: highlighted.some((focus) => focus.hasBoxTargets),
        };
  if (selected && sameMemberScope(index, selected, hovered)) {
    // The pointer is still addressing the lock, even through a wrapper or
    // member IDs. Keep its labels and mask instead of applying hover frame gates.
    return { ...selected, showLabel: true, mode: "pinned" };
  }
  return { ...hovered, mode: "hover" };
}
